import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft, ChevronRight as ChevronRightIcon, Loader2, BookOpenText, ArrowUpRight } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { trackPageView } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANONICAL_BASE = "https://cemedeiros.com.br";

/**
 * CategoryPage, Sprint B · Item 1.
 * Página SEO para 1 das 7 categorias mapeadas no backend (/api/category/:slug).
 * Inclui H1, breadcrumb, meta tags, JSON-LD CollectionPage, lista de sermões e
 * paginação. Lazy de devocionais quando aplicável.
 */
export default function CategoryPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0] || "sermoes";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const page = parseInt(searchParams.get("page") || "1", 10);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    axios
      .get(`${API}/category/${slug}?page=${page}&per_page=20`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        if (e?.response?.status === 404) navigate("/", { replace: true });
        else setErr("Não foi possível carregar esta categoria. Tente novamente em alguns instantes.");
      })
      .finally(() => setLoading(false));
    trackPageView(`/${slug}`);
  }, [slug, page, navigate]);

  // Meta tags + JSON-LD
  const jsonLd = data
    ? {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: data.h1,
        description: data.meta_description,
        url: data.canonical,
        inLanguage: "pt-BR",
        about: { "@type": "Thing", name: data.h1 },
        publisher: {
          "@type": "Person",
          name: "Carlos Medeiros",
          url: "https://cemedeiros.com.br/",
        },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: (data.breadcrumb || []).map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.label,
            item: b.url,
          })),
        },
        hasPart: (data.sermons?.items || []).slice(0, 20).map((s) => ({
          "@type": "Article",
          headline: s.title,
          url: `${CANONICAL_BASE}/pregacoes/${s.slug}`,
          articleSection: s.category,
        })),
      }
    : null;

  useDocumentMeta({
    title: data?.title,
    description: data?.meta_description,
    canonical: data?.canonical,
    url: data?.canonical,
    type: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main data-testid={`category-page-${slug}`} className="pt-32 md:pt-36 pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          {/* Breadcrumb */}
          <nav
            data-testid="category-breadcrumb"
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 mb-7"
          >
            <Link to="/" className="hover:text-[#C5A059]">Início</Link>
            <ChevronRight size={12} strokeWidth={1.7} />
            <span className="text-[#0B1E36] font-medium">
              {data?.h1 || "Categoria"}
            </span>
          </nav>

          {/* Header */}
          {!data && loading && (
            <div className="text-center py-24 text-[#0B1E36]/45">
              <Loader2 size={20} className="inline animate-spin mr-2" /> Carregando…
            </div>
          )}
          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 mb-6">
              {err}
            </div>
          )}

          {data && (
            <>
              <header className="max-w-3xl mb-12">
                <div className="flex items-center gap-2.5 text-[#C5A059] mb-3">
                  <BookOpenText size={13} strokeWidth={1.7} />
                  <span className="text-[0.62rem] uppercase tracking-[0.3em] font-medium">
                    Conteúdo organizado
                  </span>
                </div>
                <h1
                  data-testid="category-h1"
                  className="font-serif-display text-[2.2rem] md:text-[3rem] lg:text-[3.4rem] text-[#0B1E36] leading-[1.05] tracking-[-0.02em] font-medium"
                >
                  {data.h1}
                </h1>
                <p className="mt-5 text-base md:text-lg text-[#0B1E36]/70 leading-[1.7] max-w-2xl">
                  {data.intro}
                </p>
              </header>

              {/* Devotionals (when aplicable) */}
              {data.devotionals?.length > 0 && (
                <section data-testid="category-devotionals" className="mb-14">
                  <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36] mb-5">
                    Devocionais recentes
                  </h2>
                  <ul className="grid md:grid-cols-2 gap-px bg-[#0B1E36]/8">
                    {data.devotionals.slice(0, 6).map((d) => (
                      <li
                        key={d.date}
                        data-testid={`category-devotional-${d.date}`}
                        className="bg-white p-5 hover:bg-[#FAF8F4] transition-colors"
                      >
                        <Link
                          to={`/devocional/${d.date}`}
                          className="block group"
                        >
                          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#C5A059] mb-2 tabular-nums">
                            {new Date(d.date).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                          <h3 className="font-serif-display text-[1.1rem] text-[#0B1E36] leading-snug">
                            {d.theme || d.devotional?.title || d.sermon?.title}
                          </h3>
                          {d.verse_reference && (
                            <p className="mt-2 text-[0.78rem] text-[#0B1E36]/55 italic">
                              {d.verse_reference}
                            </p>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Sermons */}
              <section data-testid="category-sermons" className="mb-10">
                <div className="flex items-baseline justify-between mb-5">
                  <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36]">
                    Sermões {data.sermons.total > 0 && (
                      <span className="text-[#0B1E36]/40 text-base ml-2 tabular-nums">
                        · {data.sermons.total}
                      </span>
                    )}
                  </h2>
                </div>
                {data.sermons.items.length === 0 ? (
                  <p className="text-[#0B1E36]/45 italic">Nada por aqui ainda.</p>
                ) : (
                  <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0B1E36]/8">
                    {data.sermons.items.map((s) => (
                      <li
                        key={s.id || s.slug}
                        data-testid={`category-sermon-${s.slug}`}
                        className="bg-white hover:bg-[#FAF8F4] transition-colors"
                      >
                        <Link
                          to={`/pregacoes/${s.slug}`}
                          className="block p-5 group h-full"
                        >
                          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#C5A059] mb-2">
                            {s.category}
                          </div>
                          <h3 className="font-serif-display text-[1.05rem] text-[#0B1E36] leading-snug group-hover:text-[#C5A059] transition-colors">
                            {s.title}
                          </h3>
                          {s.verse_reference && (
                            <p className="mt-2 text-[0.78rem] text-[#0B1E36]/55">
                              {s.verse_reference}
                            </p>
                          )}
                          {s.preview && (
                            <p className="mt-3 text-[0.82rem] text-[#0B1E36]/65 line-clamp-3">
                              {s.preview}
                            </p>
                          )}
                          <span className="mt-3 inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 group-hover:text-[#C5A059]">
                            Ler sermão
                            <ArrowUpRight size={11} strokeWidth={1.8} />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Pagination */}
                {data.sermons.total_pages > 1 && (
                  <Pagination
                    page={page}
                    totalPages={data.sermons.total_pages}
                    onChange={(p) => setSearchParams({ page: String(p) })}
                  />
                )}
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  const prev = Math.max(1, page - 1);
  const next = Math.min(totalPages, page + 1);
  return (
    <nav
      data-testid="category-pagination"
      className="mt-10 flex items-center justify-between border-t border-[#0B1E36]/10 pt-6"
      aria-label="Paginação"
    >
      <button
        data-testid="category-pagination-prev"
        onClick={() => onChange(prev)}
        disabled={page <= 1}
        className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/65 hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#0B1E36]/65"
      >
        <ChevronLeft size={13} strokeWidth={1.7} />
        Anterior
      </button>
      <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 tabular-nums">
        Página {page} de {totalPages}
      </span>
      <button
        data-testid="category-pagination-next"
        onClick={() => onChange(next)}
        disabled={page >= totalPages}
        className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/65 hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#0B1E36]/65"
      >
        Próxima
        <ChevronRightIcon size={13} strokeWidth={1.7} />
      </button>
    </nav>
  );
}
